import React, { useState } from 'react';

export default function ContactForm({ title = 'Send os en besked', subtitle = 'Udfyld formularen herunder, så kontakter vi dig hurtigst muligt.' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Anti-spam honeypot
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [serverMessage, setServerMessage] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = 'Venligst indtast dit navn';
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast dit telefonnummer';
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Venligst indtast en gyldig e-mailadresse';
    }
    if (!formData.message.trim()) {
      errs.message = 'Venligst indtast din besked eller beskrivelse af forsendelsen';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setServerMessage('');

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.name,
          telefon: formData.phone,
          email: formData.email,
          besked: formData.message,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setServerMessage(errorData.message || 'Der opstod en fejl under afsendelsen. Venligst prøv igen eller ring til os.');
      }
    } catch (err) {
      setStatus('error');
      setServerMessage('Netværksfejl under afsendelse. Venligst kontakt os direkte på +45 76 12 12 30.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/90 p-6 sm:p-8">
      {title && <h3 className="text-xl sm:text-2xl font-bold text-[#0f2b48] mb-2">{title}</h3>}
      {subtitle && <p className="text-sm text-slate-600 mb-6">{subtitle}</p>}

      {status === 'success' && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-start gap-3">
          <i className="fa-solid fa-circle-check text-emerald-600 text-lg mt-0.5" aria-hidden="true" />
          <div>
            <h4 className="font-bold text-sm">Tak for din henvendelse!</h4>
            <p className="text-xs text-emerald-800 mt-0.5">
              Vi har modtaget dine oplysninger og vender tilbage hurtigst muligt. Du er også altid velkommen til at ringe på +45 76 12 12 30.
            </p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation text-red-600 text-lg mt-0.5" aria-hidden="true" />
          <div>
            <h4 className="font-bold text-sm">Der skete en fejl</h4>
            <p className="text-xs text-red-800 mt-0.5">{serverMessage}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Anti-spam honeypot - invisible to humans */}
        <input
          name="website_url"
          type="text"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          className="sr-only absolute -left-[9999px]"
          aria-hidden="true"
        />

        {/* Navn */}
        <div>
          <label htmlFor="name" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
            Navn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn / Virksomhed"
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-colors focus:outline-hidden focus:ring-2 focus:ring-sky-600 bg-white ${
              errors.name ? 'border-red-400 bg-red-50/40 text-red-900' : 'border-slate-300 text-slate-900'
            }`}
          />
          {errors.name && <p className="mt-1 text-xs text-red-600 font-medium">{errors.name}</p>}
        </div>

        {/* Telefon og Email Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Telefonnummer <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+45 12 34 56 78"
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-colors focus:outline-hidden focus:ring-2 focus:ring-sky-600 bg-white ${
                errors.phone ? 'border-red-400 bg-red-50/40 text-red-900' : 'border-slate-300 text-slate-900'
              }`}
            />
            {errors.phone && <p className="mt-1 text-xs text-red-600 font-medium">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              E-mailadresse <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="navn@virksomhed.dk"
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-colors focus:outline-hidden focus:ring-2 focus:ring-sky-600 bg-white ${
                errors.email ? 'border-red-400 bg-red-50/40 text-red-900' : 'border-slate-300 text-slate-900'
              }`}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600 font-medium">{errors.email}</p>}
          </div>
        </div>

        {/* Beskrivelse / Meddelelse */}
        <div>
          <label htmlFor="message" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
            Beskrivelse af opgave / forsendelse <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Beskriv godset, dimensioner, afhentnings- og leveringssted eller dit spørgsmål..."
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-colors focus:outline-hidden focus:ring-2 focus:ring-sky-600 bg-white ${
              errors.message ? 'border-red-400 bg-red-50/40 text-red-900' : 'border-slate-300 text-slate-900'
            }`}
          />
          {errors.message && <p className="mt-1 text-xs text-red-600 font-medium">{errors.message}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#b25d19] hover:bg-[#974d12] text-white text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
              <span>Sender besked...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane mr-2" aria-hidden="true" />
              <span>Send henvendelse</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
